/**
 * Copyright © 2026 Adnuntius AS.
 */
import {firstValueFrom} from 'rxjs';
import {Component, DoCheck, Inject, Input, OnDestroy, signal} from '@angular/core';
import * as _ from 'lodash';
import template from './team-group-analysis-page.component.html';

import {LocalNetworkInfo} from '../../../components/session/local-network-info';
import {RoleApiService} from '../role/role-api.service';
import {UserApiService} from '../user/user-api.service';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {FormsModule} from '@angular/forms';
import {UIRouterModule} from '@uirouter/angular';
import {APP_SHARED} from '../../common/app-shared';

@Component({
  standalone: true,
  imports: [...APP_SHARED, FormsModule, UIRouterModule],
  selector: 'adn-team-group-analysis-page',
  template
})
export class TeamGroupAnalysisPageComponent implements DoCheck, OnDestroy {
  @Input() model: any;
  @Input() isNew: boolean;

  private readonly rolesState = signal<any[]>([]);
  private readonly usersState = signal<any[]>([], {equal: () => false});
  private readonly otherUsersState = signal<any[]>([], {equal: () => false});
  private readonly hasNewUserState = signal(false);
  private readonly loadedState = signal(false);
  get roles(): any[] { return this.rolesState(); }
  set roles(value: any[]) { this.rolesState.set(value); }
  get users(): any[] { return this.usersState(); }
  set users(value: any[]) { this.usersState.set(value); }
  get otherUsers(): any[] { return this.otherUsersState(); }
  set otherUsers(value: any[]) { this.otherUsersState.set(value); }
  get hasNewUser(): boolean { return this.hasNewUserState(); }
  set hasNewUser(value: boolean) { this.hasNewUserState.set(value); }
  get loaded(): boolean { return this.loadedState(); }
  set loaded(value: boolean) { this.loadedState.set(value); }

  private readonly networkId: string;
  private lastLoadedModelId: string | undefined;
  private destroyed = false;

  constructor(
    private readonly roleApi: RoleApiService,
    private readonly userApi: UserApiService,
    @Inject(LocalNetworkProfile) LocalNetworkProfile: any,
    private readonly localNetworkInfo: LocalNetworkInfo = new LocalNetworkInfo()
  ) {
    this.networkId = LocalNetworkProfile.getNetworkId();
  }

  get teamScopedRoles(): any[] {
    return _.filter(this.roles, ['scope', 'TEAM']);
  }

  get displayLoaded(): boolean {
    return this.loaded;
  }

  ngDoCheck(): void {
    const modelId = this.model?.id;
    if (modelId === this.lastLoadedModelId) {
      return;
    }
    this.lastLoadedModelId = modelId;
    // Same as team analysis: starting the user load mid-CD lets `loaded`
    // flip false→true in the members-tab verify pass (NG0100).
    setTimeout(() => {
      if (this.destroyed || this.lastLoadedModelId !== modelId) {
        return;
      }
      this.refreshUsers();
    }, 0);
  }

  ngOnDestroy(): void {
    this.destroyed = true;
  }

  trackByUserId(_index: number, user: any): string {
    return user.id;
  }

  trackByOtherUserId(_index: number, user: any): string {
    return user.id;
  }

  addUser(): void {
    if (!this.otherUsers.length || !this.teamScopedRoles.length) {
      return;
    }
    this.hasNewUser = true;
    const defaultUser = this.otherUsers[0];
    this.users.unshift({
      id: defaultUser.id,
      username: defaultUser.username,
      displayName: defaultUser.displayName,
      objectState: defaultUser.objectState,
      obfuscated: defaultUser.obfuscated,
      newUser: true,
      active: true,
      edited: true,
      teamGroupRole: this.teamScopedRoles[0]
    });
  }

  newUserChange(user: any): void {
    const otherUser = _.find(this.otherUsers, ['id', user.id]);
    if (!otherUser) {
      return;
    }
    user.username = otherUser.username;
    user.displayName = otherUser.displayName;
    user.objectState = otherUser.objectState;
    user.obfuscated = otherUser.obfuscated;
  }

  editUser(user: any): void {
    user.edited = true;
    user.active = true;
    user.prevRole = user.teamGroupRole;
  }

  cancelUser(user: any, index: number): void {
    user.edited = false;
    user.teamGroupRole = user.prevRole;
    user.failSave = false;
    if (user.newUser) {
      _.pullAt(this.users, index);
      this.hasNewUser = false;
    }
  }

  saveUser(user: any): void {
    firstValueFrom(this.userApi.get(user.id)).then((apiUser: any) => {
      const networkRoles = _.find(apiUser.userRoles.rolesByNetwork, ['network.tagId', this.networkId]);
      if (networkRoles && networkRoles.roles) {
        const teamGroup = _.find(networkRoles.roles, ['teamGroup.id', this.model.id]);
        if (user.active) {
          const roleUpdate = {id: user.teamGroupRole.id};
          if (teamGroup) {
            teamGroup.role = roleUpdate;
          } else {
            networkRoles.roles.push({teamGroup: {id: this.model.id}, role: roleUpdate});
          }
        } else {
          const indexEntryRemove = _.findIndex(networkRoles.roles, ['teamGroup.id', this.model.id]);
          _.pullAt(networkRoles.roles, indexEntryRemove);
        }
      }
      return firstValueFrom(this.userApi.savePartial(apiUser, 'userRoles'));
    }).then(() => {
      user.edited = false;
      user.failSave = false;
      if (user.newUser) {
        user.newUser = false;
        _.remove(this.otherUsers, (otherUser: any) => otherUser.id === user.id);
      } else if (!user.active) {
        _.remove(this.users, (item: any) => item.id === user.id);
        this.otherUsers.push(user);
      }
      this.users = this.users;
      this.otherUsers = this.otherUsers;
      this.hasNewUser = false;
    }).catch(() => {
      user.failSave = true;
      this.users = this.users;
    });
  }

  private refreshUsers(): void {
    if (!this.model?.id) {
      this.roles = [];
      this.users = [];
      this.otherUsers = [];
      this.hasNewUser = false;
      this.loaded = true;
      return;
    }

    this.loaded = false;
    firstValueFrom(this.roleApi.listOptions()).then((rolePage) => {
      return firstValueFrom(this.userApi.listOptions({
        concise: true,
        pageSize: this.localNetworkInfo.getInfo('userCount')
      })).then((userPage) => {
        return {roles: rolePage.results, allUsers: userPage.results};
      });
    }).then(({roles, allUsers}) => {
      this.roles = roles;
      this.hasNewUser = false;
      _.forEach(allUsers, (user: any) => {
        user.obfuscated = user.username.indexOf('@') < 0 && user.username.indexOf('*') > 0;
        user.teamGroupRole = null;
      });

      const selection = (user: any) => {
        const rolesByNetwork = _.find(user.userRoles.rolesByNetwork, ['network.tagId', this.networkId]);
        if (!rolesByNetwork) {
          return false;
        }
        const teamGroupRole = _.find(rolesByNetwork.roles, ['teamGroup.id', this.model.id]);
        if (teamGroupRole) {
          user.teamGroupRole = _.find(this.roles, ['id', teamGroupRole.role.id]);
        }
        return user.teamGroupRole;
      };

      this.users = _.filter(allUsers, selection);
      this.otherUsers = _.reject(allUsers, selection);
      this.loaded = true;
    }).catch(() => {
      this.roles = [];
      this.users = [];
      this.otherUsers = [];
      this.hasNewUser = false;
      this.loaded = true;
    });
  }
}
